import {
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useEffect,
} from "react";
import { useTranslation, Trans } from "react-i18next";
import Pack from "../Pack";
import styles from "../../css/heatmaps/HeroHM.module.css";
import ButtonBtn from "../buttons/ButtonBtn";

// Importy obrázků
import heroImagePNG from "../../img/heatmaps/header_heatmap_border.png";
import heroImageWebP from "../../img/heatmaps/header_heatmap_border.webp";
import heroImageAvif from "../../img/heatmaps/header_heatmap_border.avif";
import flameIcon from "../../img/list.svg";

const HeroHM = ({ notLoggedClick }) => {
  const { t } = useTranslation();
  const contentHeroHM = t("heatmaps.hero", { returnObjects: true });

  // Refy pro sledování pozice spanu (desktop + mobile)
  const spanRefDesktop = useRef(null);
  const spanRefMobile = useRef(null);

  // Stav pro uložení pozice rámečku
  const [borderPosition, setBorderPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    opacity: 0, // Začíná neviditelný
  });

  // Memoizovaná funkce pro aktualizaci pozice — vybere viditelný přístupný heading (desktop/mobile)
  const updatePosition = useCallback(() => {
    const candidates = [spanRefDesktop.current, spanRefMobile.current].filter(
      Boolean
    );

    for (const span of candidates) {
      const spanRect = span.getBoundingClientRect();
      const parentBox =
        span.closest(`.${styles.box}`) ||
        span.closest(`.${styles.desktopVersion}`);
      if (!parentBox) continue;
      const parentRect = parentBox.getBoundingClientRect();
      if (!spanRect.width || !spanRect.height) continue;

      // Výpočet středu spanu relativně k parentu
      const centerX = spanRect.left - parentRect.left + spanRect.width / 2;
      const centerY = spanRect.top - parentRect.top + spanRect.height / 2;
      const targetWidth = spanRect.width * 1.25;
      const targetHeight = spanRect.height * 1.6;

      setBorderPosition({
        top: centerY, // nyní obsahuje střed (přejmenování kvůli zachování stylů níže nebude nutné měnit klíče)
        left: centerX,
        width: targetWidth,
        height: targetHeight,
        opacity: 1,
      });
      return;
    }
    setBorderPosition((prev) => ({ ...prev, opacity: 0 }));
  }, []);

  // Efekt pro měření pozice po vykreslení a při změně velikosti okna
  useLayoutEffect(() => {
    // Čekáme, až se CSS animace/přechody ustálí, pokud nějaké jsou
    const timeoutId = setTimeout(updatePosition, 50);

    window.addEventListener("resize", updatePosition);

    // Úklidová funkce
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition]);

  // Handler pro kliknutí na celý hero (desktop)
  const handleHeroClick = useCallback(() => {
    if (typeof notLoggedClick === "function") {
      notLoggedClick();
    }
  }, [notLoggedClick]);

  const handleHeroKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleHeroClick();
      }
    },
    [handleHeroClick]
  );

  // Přepínač renderované varianty podle šířky okna, aby byl v DOM vždy jen jeden <h1>
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(min-width: 1064px)");
    const onChange = (e) => setIsDesktop(e.matches);
    // inicializuj a naslouchej změnám
    setIsDesktop(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  // Při přepnutí varianty přepočítej pozici heatmapy
  useEffect(() => {
    const id = setTimeout(() => updatePosition(), 50);
    return () => clearTimeout(id);
  }, [isDesktop, updatePosition]);

  return (
    <Pack
      dataComponent={"hero-heatmaps"}
      articleInlineStyle={{ position: "relative" }}
    >
      {isDesktop ? (
        <div
          className={styles.desktopVersion}
          role="button"
          tabIndex={0}
          aria-label={contentHeroHM?.btn || "Akce"}
          onClick={handleHeroClick}
          onKeyDown={handleHeroKeyDown}
        >
          <picture>
            <source srcSet={heroImageAvif} type="image/avif" />
            <source srcSet={heroImageWebP} type="image/webp" />
            <source srcSet={heroImagePNG} type="image/png" />
            <img
              alt="hero obrázek PermoniQ heatmapy"
              width={1280}
              height={475}
              fetchpriority="high"
              src={heroImagePNG}
            />
          </picture>

          {/* Heatmap overlay pro desktop – zarovnán na zvýrazněný span */}
          <div
            className={styles.asyncBorder}
            style={{
              top: `${borderPosition.top}px`,
              left: `${borderPosition.left}px`,
              width: `${borderPosition.width}px`,
              height: `${borderPosition.height}px`,
              opacity: borderPosition.opacity,
            }}
          />

          {/* H1 nadpis (desktop overlay) – jeden H1 pro obě části */}
          <div className={styles.headingBox}>
            <h1>
              <span className={styles.topLine}>
                {t(contentHeroHM.heading1)}
              </span>
              <br />
              <Trans
                i18nKey={t(contentHeroHM.heading2)}
                parent={"span"}
                className={styles.bottomLine}
                components={{
                  1: <span ref={spanRefDesktop} />,
                }}
              />
            </h1>
          </div>

          <div className={styles.btnBox}>
            <ButtonBtn
              className={styles.btn}
              onClick={(e) => {
                e.stopPropagation();
                notLoggedClick && notLoggedClick();
              }}
            >
              {contentHeroHM.btn}
            </ButtonBtn>
            <p>{contentHeroHM.freeTest}</p>
          </div>
        </div>
      ) : (
        <div className={styles.mobileVersion}>
          <div
            className={styles.box}
            role="button"
            tabIndex={0}
            aria-label={contentHeroHM?.btn || "Akce"}
            onClick={handleHeroClick}
            onKeyDown={handleHeroKeyDown}
          >
            <div
              className={styles.asyncBorder}
              style={{
                top: `${borderPosition.top}px`,
                left: `${borderPosition.left}px`,
                width: `${borderPosition.width}px`,
                height: `${borderPosition.height}px`,
                opacity: borderPosition.opacity,
              }}
            />

            <p className={styles.firstTest}>
              <img src={flameIcon} alt="" />
              {t(contentHeroHM.firstTest)}
            </p>

            <div className={styles.headingBox}>
              <h1>
                <span className={styles.topLine}>
                  {t(contentHeroHM.heading1)}
                </span>
                <br />
                <Trans
                  i18nKey={t(contentHeroHM.heading2)}
                  parent={"span"}
                  className={styles.bottomLine}
                  components={{
                    1: <span ref={spanRefMobile} />, // Připojení refu k cílovému spanu (mobile)
                  }}
                />
              </h1>
            </div>

            <div className={styles.btnBoxMobile}>
              <ButtonBtn
                className={styles.btn}
                onClick={(e) => {
                  e.stopPropagation();
                  notLoggedClick && notLoggedClick();
                }}
              >
                {contentHeroHM.btn}
              </ButtonBtn>
              <p>{contentHeroHM.freeTest}</p>
            </div>
          </div>
        </div>
      )}
    </Pack>
  );
};

export default HeroHM;
